import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsTreemap from "highcharts/modules/treemap";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { ExposureCardConfig, ExposureCardData, ExposureDimension, ExposureMeasure } from "store/types";

HighchartsTreemap(Highcharts);

const COLORS = ["#0F5D5E", "#1A7A6E", "#2E9E8E", "#55BFB0", "#88D5CA", "#B2E8DF", "#D6F4EF", "#486581"];

const DIMENSION_LABELS: Record<ExposureDimension, string> = {
  assetClass: "Asset Class",
  Geography: "Geography",
  Strategy: "Strategy",
  Vintage: "Vintage"
};

const MEASURE_LABELS: Record<ExposureMeasure, string> = {
  exposure: "Total Exposure",
  adjNav: "Adjusted NAV",
  unfundedCommitment: "Unfunded Commitment"
};

const fmtValue = (v: number, postfix?: string) => {
  if (postfix === "%" || postfix === "pct") return `${v.toFixed(1)}%`;
  return `${postfix ?? "$"}${(v / 1_000_000).toFixed(1)}M`;
};
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max - 1) + "\u2026" : s);

interface Props {
  config: ExposureCardConfig;
  data?: ExposureCardData;
  loading?: boolean;
}

export const ExposureBreakdownCard: React.FC<Props> = ({ config, data, loading }) => {
  const { isEditMode } = useCardSize();
  const { dimension, chartType, measure } = config;
  const dimensionLabel = DIMENSION_LABELS[dimension];
  const measureLabel = MEASURE_LABELS[measure ?? "exposure"];

  const entries: ChartEntry[] = data
    ? data.map((d) => ({ name: d.label, value: d.value, pct: d.percentage, postfix: d.postfix }))
    : [];

  const headerText = `${measureLabel} by ${dimensionLabel}`;

  const header = (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        pl: "12px",
        mb: "4px"
      }}
    >
      {headerText}
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <LinearProgress sx={{ mt: 4, mx: 2 }} />
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", p: 2 }}>No data available</Typography>
      </Box>
    );
  }

  if (chartType === "donut" || chartType === "pie") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <DonutPieChart
          entries={entries}
          dimensionLabel={dimensionLabel}
          innerSize={chartType === "donut" ? "45%" : "0%"}
          isEditMode={isEditMode}
        />
      </Box>
    );
  }

  if (chartType === "bar") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <HorizontalBarChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
      </Box>
    );
  }

  if (chartType === "column") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <VerticalBarChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
      </Box>
    );
  }

  if (chartType === "treemap") {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        {header}
        <TreemapChart entries={entries} dimensionLabel={dimensionLabel} isEditMode={isEditMode} />
      </Box>
    );
  }

  // Fallback
  return (
    <Box sx={{ p: "16px 8px 8px" }}>
      {header}
      <Typography sx={{ fontSize: 11, color: "#9CA3AF", p: 2 }}>Unsupported chart type</Typography>
    </Box>
  );
};

// ─── Chart entry type ────────────────────────────────────────────────────────

interface ChartEntry {
  name: string;
  value: number;
  pct: number;
  postfix: string;
}

// ─── Donut / Pie ─────────────────────────────────────────────────────────────

interface DonutPieChartProps {
  entries: ChartEntry[];
  dimensionLabel: string;
  innerSize: string;
  isEditMode: boolean;
}

const DonutPieChart: React.FC<DonutPieChartProps> = ({ entries, dimensionLabel, innerSize, isEditMode }) => {
  const chartData = entries.map((e, i) => ({
    name: e.name,
    y: e.value,
    color: COLORS[i % COLORS.length]
  }));
  const useVerticalLegend = chartData.length > 5 || Math.max(...chartData.map((d) => d.name.length), 0) > 12;

  const options: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 260,
      marginTop: 8,
      ...(useVerticalLegend ? { marginRight: 92 } : {}),
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    tooltip: {
      pointFormatter() {
        const entry = entries.find((e) => e.name === this.name);
        return `<b>${fmtValue(this.y ?? 0, entry?.postfix)}</b> (${fmtPct(entry?.pct ?? 0)})`;
      }
    },
    plotOptions: {
      pie: {
        innerSize,
        dataLabels: { enabled: false },
        showInLegend: true,
        center: useVerticalLegend ? ["40%", "45%"] : ["50%", "45%"],
        size: useVerticalLegend ? "58%" : "70%"
      }
    },
    legend: useVerticalLegend
      ? {
          layout: "vertical",
          align: "right",
          verticalAlign: "middle",
          width: 80,
          itemMarginBottom: 4,
          itemStyle: { fontSize: "11px", fontWeight: "normal", color: "#374151" },
          symbolRadius: 4,
          labelFormatter() {
            return truncate(this.name, 10);
          }
        }
      : {
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          itemStyle: { fontSize: "11px", fontWeight: "normal", color: "#374151" },
          symbolRadius: 4,
          margin: 8,
          itemDistance: 12,
          labelFormatter() {
            return truncate(this.name, 14);
          }
        },
    series: [
      {
        type: "pie",
        name: dimensionLabel,
        data: chartData
      }
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ─── Horizontal Bar ──────────────────────────────────────────────────────────

interface BarChartProps {
  entries: ChartEntry[];
  dimensionLabel: string;
  isEditMode: boolean;
}

const HorizontalBarChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const maxLabelLen = Math.max(...entries.map((e) => e.name.length), 0);
  const dynamicMarginLeft = Math.min(Math.max(maxLabelLen * 7, 60), 120);

  const options: Highcharts.Options = {
    chart: {
      type: "bar",
      height: Math.max(entries.length * 36 + 60, 180),
      marginTop: 12,
      marginRight: 16,
      marginLeft: dynamicMarginLeft,
      marginBottom: 24,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    xAxis: {
      categories: entries.map((e) => e.name),
      labels: {
        style: { fontSize: "11px", color: "#374151", fontWeight: "600" },
        formatter() {
          const v = String(this.value);
          return v.length > 14 ? v.slice(0, 13) + "\u2026" : v;
        }
      },
      lineColor: "#E5E7EB",
      tickColor: "transparent"
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter() {
          return fmtPct(this.value as number);
        },
        style: { fontSize: "10px", color: "#9CA3AF" }
      },
      max: 100,
      gridLineColor: "#F3F4F6"
    },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.x);
        return `<b>${this.x}</b><br/>${fmtPct(this.y ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    plotOptions: {
      bar: {
        borderWidth: 0,
        borderRadius: 2,
        pointWidth: 16,
        colorByPoint: true,
        colors: COLORS
      }
    },
    legend: { enabled: false },
    series: [
      {
        type: "bar",
        name: dimensionLabel,
        data: entries.map((e) => e.pct)
      }
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ─── Vertical Bar (Column) ───────────────────────────────────────────────────

const VerticalBarChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 260,
      marginTop: 12,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    xAxis: {
      categories: entries.map((e) => e.name),
      labels: {
        rotation: entries.length > 4 ? -45 : 0,
        style: { fontSize: "11px", color: "#374151", fontWeight: "600" },
        formatter() {
          const v = String(this.value);
          return v.length > 12 ? v.slice(0, 11) + "\u2026" : v;
        }
      },
      lineColor: "#E5E7EB",
      tickColor: "transparent"
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter() {
          return fmtPct(this.value as number);
        },
        style: { fontSize: "10px", color: "#9CA3AF" }
      },
      max: 100,
      gridLineColor: "#F3F4F6"
    },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.x);
        return `<b>${this.x}</b><br/>${fmtPct(this.y ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        borderRadius: 2,
        colorByPoint: true,
        colors: COLORS
      }
    },
    legend: { enabled: false },
    series: [
      {
        type: "column",
        name: dimensionLabel,
        data: entries.map((e) => e.pct)
      }
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// ─── Treemap ─────────────────────────────────────────────────────────────────

const TreemapChart: React.FC<BarChartProps> = ({ entries, dimensionLabel, isEditMode }) => {
  const treemapData = entries.map((e, i) => ({
    name: e.name,
    value: e.value,
    color: COLORS[i % COLORS.length]
  }));

  const options: Highcharts.Options = {
    chart: {
      height: 260,
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    tooltip: {
      formatter() {
        const entry = entries.find((e) => e.name === this.key);
        return `<b>${this.key}</b><br/>${fmtPct(entry?.pct ?? 0)} (${fmtValue(entry?.value ?? 0, entry?.postfix)})`;
      }
    },
    series: [
      {
        type: "treemap",
        layoutAlgorithm: "squarified",
        name: dimensionLabel,
        data: treemapData,
        dataLabels: {
          enabled: true,
          style: { fontSize: "11px", fontWeight: "600", color: "#FFFFFF", textOutline: "none" },
          formatter() {
            const entry = entries.find((e) => e.name === this.key);
            return `${truncate(this.key ?? "", 16)}<br/>${fmtPct(entry?.pct ?? 0)}`;
          }
        }
      } as Highcharts.SeriesTreemapOptions
    ]
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};
